import { useState, useEffect, useCallback, useMemo } from 'react';
import update from 'immutability-helper';
import {
  useGetMenuSectionProductsAdminQuery,
  useFilteredSpecialsQuery,
  GqlSpecials,
  MenuSectionType,
  GqlMenuSection,
} from 'types/graphql';
import { useStores } from 'src/hooks/use-stores';
import useErnie from 'shared/hooks/use-ernie';
import { useRecentlyViewedEnabled } from './hooks/use-recently-viewed-enabled';
import { useGetCollections } from './hooks/use-get-collections';
import { MenuCustomizationData, useUpdateMenuSections } from '../data-access';
import { LocalSection, Products, Offers, DynamicSectionType, MenuSectionOption } from './homepage-form.types';
import { DYNAMIC_SECTIONS, DEFAULT_SECTIONS } from './homepage-form.constants';
import {
  filterMenuSections,
  getAvailableMenuSectionTypes,
  createNewDynamicLocalSection,
  getUpdateParams,
} from './homepage-form.utils';

type UseGetItemDataReturn = {
  products: Products | null;
  offers: Offers | null;
  itemsLoading: boolean;
};

function useGetItemData(dispensaryId: string): UseGetItemDataReturn {
  const { apolloClient } = useStores();

  const { data: productsData, loading: productsLoading } = useGetMenuSectionProductsAdminQuery({
    variables: {
      filter: {
        dispensaryId,
        sortBy: 'popularSortIdx',

        Status: 'Active',
      },
    },
    client: apolloClient,
    fetchPolicy: 'network-only',
  });

  const { data: specialsData, loading: specialsLoading } = useFilteredSpecialsQuery({
    variables: {
      specialsFilter: {
        dispensaryId,
        current: true,
      },
    },
    client: apolloClient,
    fetchPolicy: 'network-only',
  });

  const products = useMemo(() => productsData?.filteredProducts?.products ?? null, [productsData]);
  const allSpecials = useMemo(() => specialsData?.filteredSpecials?.specials ?? null, [specialsData]);
  const offers = useMemo(() => allSpecials?.filter((special: GqlSpecials) => special.specialType === 'bogo'), [
    allSpecials,
  ]);

  return {
    products,
    offers,
    itemsLoading: productsLoading || specialsLoading,
  };
}

type UseHomepageFormProps = {
  data: MenuCustomizationData;
};

type UseHomepageFormReturn = {
  availableMenuSectionTypes: MenuSectionOption[];
  currentSection: LocalSection | null;
  handleAddDefaultSection: (newSection: LocalSection) => void;
  handleAddSection: (sectionTypeKey: MenuSectionType) => void;
  handleCloseModal: () => void;
  handleDrop: () => void;
  handleEditSection: (id: string, type: MenuSectionType) => void;
  handleMoveOption: (dragIndex: number, hoverIndex: number) => void;
  handlePublishSections: () => Promise<void>;
  handleRemoveMenuSection: (menuSectionId: string) => void;
  handleResetSections: () => void;
  isDirty: boolean;
  itemsLoading: boolean;
  localMenuSections: LocalSection[] | null;
  previewMenuSections: GqlMenuSection[];
  products: Products | null;
  showModalType: MenuSectionType | null;
};

export function useHomepageForm({ data }: UseHomepageFormProps): UseHomepageFormReturn {
  const { menuSections } = data;
  const { UI } = useStores();
  const showErnie = useErnie();
  const [initialSections, setInitialSections] = useState<LocalSection[] | null>(null);
  const [currentSection, setCurrentSection] = useState<LocalSection | null>(null);
  const [availableMenuSectionTypes, setAvailableMenuSectionTypes] = useState<MenuSectionOption[]>([]);
  const [showModalType, setShowModalType] = useState<MenuSectionType | null>(null);
  const [localMenuSections, setLocalMenuSections] = useState<LocalSection[] | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const updateData = useUpdateMenuSections();

  const { products, offers, itemsLoading } = useGetItemData(UI.dispensary.id ?? '');

  const showRecentlyViewedCarousel = useRecentlyViewedEnabled();

  const { collections } = useGetCollections();

  const collectionIds = collections.map((collection) => collection.id);
  const localMenuSectionCollectionIds = localMenuSections
    ?.filter((section) => section.sectionType === MenuSectionType.collection)
    .map((section) => section.collectionId);
  const unusedCollectionIds = useMemo(() => {
    if (!localMenuSectionCollectionIds) {
      return collectionIds;
    }

    return collectionIds.filter((id) => !localMenuSectionCollectionIds.includes(id));
  }, [collectionIds, localMenuSectionCollectionIds]);

  const initialQueryResultMenuSections = useMemo(() => {
    let filteredMenuSections = menuSections;
    if (!showRecentlyViewedCarousel) {
      filteredMenuSections = menuSections.filter((section) => section.sectionType !== MenuSectionType.recentlyViewed);
    }

    return filterMenuSections(filteredMenuSections, { products, offers });
  }, [menuSections, showRecentlyViewedCarousel, products, offers]);

  const handleSetInitialSections = useCallback((newSections: LocalSection[]) => setInitialSections(newSections), []);

  useEffect(() => {
    handleSetInitialSections(initialQueryResultMenuSections);
    if (!localMenuSections && !itemsLoading) {
      setLocalMenuSections(initialQueryResultMenuSections);
    }
  }, [handleSetInitialSections, initialQueryResultMenuSections, itemsLoading, localMenuSections]);

  const filterAvailableMenuSections = useCallback(
    (sections: MenuSectionOption[]): MenuSectionOption[] =>
      sections.filter((section) => {
        if (!showRecentlyViewedCarousel && section.key === MenuSectionType.recentlyViewed) {
          return false;
        }

        if (!unusedCollectionIds.length && section.key === MenuSectionType.collection) {
          return false;
        }

        return true;
      }),
    [showRecentlyViewedCarousel, unusedCollectionIds.length]
  );

  useEffect(() => {
    if (localMenuSections) {
      const availableSections = filterAvailableMenuSections(getAvailableMenuSectionTypes(localMenuSections));

      setAvailableMenuSectionTypes(availableSections);
    } else {
      const availableSections = filterAvailableMenuSections(
        getAvailableMenuSectionTypes(initialQueryResultMenuSections)
      );

      setAvailableMenuSectionTypes(availableSections);
    }
  }, [filterAvailableMenuSections, initialQueryResultMenuSections, localMenuSections]);

  useEffect(() => {
    setLocalMenuSections(initialSections);
  }, [showRecentlyViewedCarousel, initialSections]);

  const handlePublishSections = async (): Promise<void> => {
    const updateParams = getUpdateParams(UI.dispensary.id ?? '', initialSections, localMenuSections);

    try {
      await updateData({
        variables: {
          ...updateParams,
        },
      });

      showErnie('Your menu sections have been updated', 'success');
      setIsDirty(false);
    } catch (error) {
      showErnie('Something went wrong, please try again.', 'danger');
      console.error(error);
    }
  };

  const handleResetSections = (): void => {
    setLocalMenuSections(initialSections);
  };

  const handleAddSection = (sectionTypeKey: MenuSectionType): void => {
    if (DEFAULT_SECTIONS.includes(sectionTypeKey)) {
      setShowModalType(sectionTypeKey);
      return;
    }

    if (UI.dispensary.id && DYNAMIC_SECTIONS.includes(sectionTypeKey)) {
      const newSection = createNewDynamicLocalSection(UI.dispensary.id, sectionTypeKey as DynamicSectionType, {
        products,
        offers,
      });

      if (newSection) {
        if (!localMenuSections) {
          setLocalMenuSections([newSection]);
        } else {
          setLocalMenuSections([...localMenuSections, newSection]);
        }
      }
    }

    setIsDirty(true);
  };

  const handleAddDefaultSection = (newSection: LocalSection): void => {
    if (!localMenuSections) {
      setLocalMenuSections([newSection]);
    } else {
      const foundSectionIndex = localMenuSections.findIndex((section) => section.id === newSection.id);

      if (foundSectionIndex > -1) {
        const updatedSections = [...localMenuSections];
        updatedSections.splice(foundSectionIndex, 1, newSection);
        setLocalMenuSections(updatedSections);
      } else {
        setLocalMenuSections([...localMenuSections, newSection]);
      }
    }

    setIsDirty(true);
  };

  const handleDrop = (): void => {
    if (localMenuSections) {
      const sortedLocalSections = localMenuSections.map((section, index) => ({ ...section, position: index + 1 }));
      setLocalMenuSections(sortedLocalSections);
    }

    setIsDirty(true);
  };

  const handleMoveOption = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const draggedMenuSection = localMenuSections?.[dragIndex];
      if (draggedMenuSection) {
        const newSectionOrder = update(localMenuSections, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, draggedMenuSection],
          ],
        });

        setLocalMenuSections(newSectionOrder);
      }
    },
    [localMenuSections]
  );

  const handleRemoveMenuSection = (menuSectionId: string): void => {
    if (localMenuSections) {
      const newLocalSections = localMenuSections
        .filter((section) => section.id !== menuSectionId)
        .map((section, index) => ({ ...section, position: index + 1 }));

      setLocalMenuSections(newLocalSections);
    }
    setIsDirty(true);
  };

  const handleEditSection = (id: string, type: MenuSectionType): void => {
    const sectionToEdit = localMenuSections?.find((section) => section.id === id) ?? null;
    setShowModalType(type);
    setCurrentSection(sectionToEdit);
  };

  const handleCloseModal = (): void => {
    setShowModalType(null);
    setCurrentSection(null);
  };

  const previewMenuSections =
    localMenuSections?.map(
      (section: LocalSection) => ({ ...section, dispensary: UI.dispensary, linkLabel: '' } as GqlMenuSection)
    ) ?? [];

  return {
    availableMenuSectionTypes,
    currentSection,
    handleAddDefaultSection,
    handleAddSection,
    handleCloseModal,
    handleDrop,
    handleEditSection,
    handleMoveOption,
    handlePublishSections,
    handleRemoveMenuSection,
    handleResetSections,
    isDirty,
    itemsLoading,
    localMenuSections,
    previewMenuSections,
    products,
    showModalType,
  };
}
